/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Toll as CreditIcon,
  Person as ExpertIcon,
  SmartToy as AgentIcon,
  Storage as SystemIcon,
} from '@mui/icons-material';
import { billingApi, UsageBreakdown } from '../../api/billing';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color = 'primary.main' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ color, mr: 1.5 }}>{icon}</Box>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const formatCredits = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(Number(value))) {
    return '-';
  }
  return `${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })} credits`;
};

const CostDashboard: React.FC = () => {
  const [days, setDays] = useState(30);
  const [breakdownTab, setBreakdownTab] = useState(0);

  const { data, isLoading, error } = useQuery<UsageBreakdown>({
    queryKey: ['cost-insights', days],
    queryFn: () => billingApi.getUsage(days),
  });

  const summary = useMemo(() => {
    if (!data) {
      return null;
    }

    const systemCredits = data.systems.reduce((total, system) => total + system.total_cost, 0);
    const systemRuns = data.systems.reduce((total, system) => total + system.execution_count, 0);
    const agentCredits = data.agents.reduce((total, agent) => total + agent.total_cost, 0);
    const agentRuns = data.agents.reduce((total, agent) => total + agent.execution_count, 0);
    const expertRuns = data.experts.reduce((total, expert) => total + expert.execution_count, 0);
    const topSystem = data.systems[0] ?? null;

    return {
      systemCredits,
      systemRuns,
      agentCredits,
      agentRuns,
      expertRuns,
      topSystem,
    };
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load credit insights: {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  const hasAnyCostData = data.systems.length > 0 || data.experts.length > 0 || data.agents.length > 0;

  const systemChartData = data.systems.map((system) => ({
    name: system.system_name.length > 20 ? `${system.system_name.slice(0, 20)}…` : system.system_name,
    credits: system.total_cost,
    executions: system.execution_count,
  }));

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Credit usage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track where credits are being consumed across systems and assistants in one period view.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Period</InputLabel>
          <Select value={days} label="Period" onChange={(event) => setDays(Number(event.target.value))}>
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={14}>Last 14 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {!hasAnyCostData && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No credit usage data is available yet. Run a system or assistant to start building usage insights.
        </Alert>
      )}

      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="System Credits"
              value={formatCredits(summary.systemCredits)}
              subtitle={`${summary.systemRuns.toLocaleString()} system runs`}
              icon={<SystemIcon fontSize="large" />}
              color="primary.main"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Assistant Credits"
              value={formatCredits(summary.agentCredits)}
              subtitle={`${summary.agentRuns.toLocaleString()} assistant runs`}
              icon={<AgentIcon fontSize="large" />}
              color="secondary.main"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Expert Executions"
              value={summary.expertRuns.toLocaleString()}
              subtitle="Expert-level credit allocation is not available"
              icon={<ExpertIcon fontSize="large" />}
              color="info.main"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Top System"
              value={summary.topSystem ? formatCredits(summary.topSystem.total_cost) : formatCredits(0)}
              subtitle={summary.topSystem?.system_name || `No system credits in the last ${days} days`}
              icon={<CreditIcon fontSize="large" />}
              color="success.main"
            />
          </Grid>
        </Grid>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Credits by System
          </Typography>
          {systemChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={systemChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value: number) => `${value.toFixed(2)} cr`} />
                <RechartsTooltip formatter={(value: number) => formatCredits(value)} />
                <Legend />
                <Bar dataKey="credits" name="Credits" fill="#2196f3" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'action.hover',
                px: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No system credit usage for the selected period yet. This chart will populate after your first AI system runs.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                Breakdowns
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compare detailed credit rows by system or assistant without stacking multiple tables on the page.
              </Typography>
            </Box>
          </Box>

          <Tabs
            value={breakdownTab}
            onChange={(_, nextTab) => setBreakdownTab(nextTab)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mt: 2 }}
          >
            <Tab label="Systems" />
            <Tab label="Experts" />
            <Tab label="Assistants" />
          </Tabs>

          <TabPanel value={breakdownTab} index={0}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>System</TableCell>
                    <TableCell align="right">Credits</TableCell>
                    <TableCell align="right">Runs</TableCell>
                    <TableCell align="right">Avg / Run</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.systems.length > 0 ? (
                    data.systems.map((system) => (
                      <TableRow key={system.system_id} hover>
                        <TableCell>{system.system_name}</TableCell>
                        <TableCell align="right">{formatCredits(system.total_cost)}</TableCell>
                        <TableCell align="right">{system.execution_count}</TableCell>
                        <TableCell align="right">
                          {formatCredits(system.execution_count > 0 ? system.total_cost / system.execution_count : 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No AI system credits have been recorded in this period yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={breakdownTab} index={1}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Expert</TableCell>
                    <TableCell align="right">Credits</TableCell>
                    <TableCell align="right">Executions</TableCell>
                    <TableCell align="right">Avg Credits</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.experts.length > 0 ? (
                    data.experts.map((expert) => (
                      <TableRow key={expert.expert_id} hover>
                        <TableCell>{expert.expert_name}</TableCell>
                        <TableCell align="right">{formatCredits(expert.total_cost)}</TableCell>
                        <TableCell align="right">{expert.execution_count}</TableCell>
                        <TableCell align="right">
                          {formatCredits(expert.execution_count > 0 ? expert.total_cost / expert.execution_count : 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          Expert-level credit allocation is not available for this period yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={breakdownTab} index={2}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Assistant</TableCell>
                    <TableCell align="right">Credits</TableCell>
                    <TableCell align="right">Executions</TableCell>
                    <TableCell align="right">Avg Credits</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.agents.length > 0 ? (
                    data.agents.map((agent) => (
                      <TableRow key={agent.agent_id} hover>
                        <TableCell>{agent.agent_name}</TableCell>
                        <TableCell align="right">{formatCredits(agent.total_cost)}</TableCell>
                        <TableCell align="right">{agent.execution_count}</TableCell>
                        <TableCell align="right">
                          {formatCredits(agent.execution_count > 0 ? agent.total_cost / agent.execution_count : 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No assistant credits have been recorded in this period yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CostDashboard;
